import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import MsWordIcon from 'assets/icons/MsWordIcon'
import MsExcelIcon from 'assets/icons/MsExcelIcon'
import { Typography } from '@material-ui/core'
import i18n from 'common/util/internationalization'

export type OptionType = 'minutesOnly' | 'minutesWithActions' | 'actionsOnly'

interface ExportOptionsProps {
    exportOption: OptionType | ''
    setExportOption: (value: OptionType) => void
    setShouldShowTemplateList: (show: boolean) => void
}

const useStyles = makeStyles((theme) => ({
    container: {
        marginTop: theme.spacing(2),
        padding: '0 0 0 24px'
    },
    templateTitle: {
        fontSize: '16px',
        fontFamily: 'Source Sans Pro',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '20px', // 125%
        marginBottom: '8px'
    },
    contentContainer: {
        display: 'flex',
        gap: '12px',
        marginTop: '8px',
        alignItems: 'center',
        alignSelf: 'stretch'
    },
    card: {
        flex: '1 1 0',
        border: '1px solid #E6E6E6',
        cursor: 'pointer',
        display: 'flex',
        padding: '16px',
        minWidth: '196px',
        boxSizing: 'border-box',
        minHeight: '108px',
        textAlign: 'center',
        transition: 'all 0.3s ease',
        alignItems: 'flex-start',
        borderRadius: 8,
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: '#fff',
        gap: '12px',
        alignSelf: 'stretch',
        outline: 'none', // Remove default outline
        '&:focus': {
            outline: '2px solid #455D82',
            outlineOffset: '2px',
            borderColor: '#455D82'
        },
        '&:focus-visible': {
            outline: '2px solid #455D82',
            outlineOffset: '2px',
            borderColor: '#455D82'
        }
    },
    selectedCard: {
        borderColor: '#455D82'
        // boxShadow: `0 0 0 2px #455D82`,
    }
}))

const ExportOptions: React.FC<ExportOptionsProps> = ({
    exportOption,
    setExportOption,
    setShouldShowTemplateList
}) => {
    const classes = useStyles()

    // Set default if exportOption is empty
    React.useEffect(() => {
        if (!exportOption) {
            setExportOption('minutesOnly')
            setShouldShowTemplateList(true)
        }
    }, [exportOption, setExportOption, setShouldShowTemplateList])

    const options: { value: OptionType; label: string; icon: JSX.Element }[] = [
        {
            label: i18n.t('EXPORT_OPTIONS_MINUTES_ONLY'),
            value: 'minutesOnly',
            icon: <MsWordIcon />
        },
        {
            label: i18n.t('EXPORT_OPTIONS_MINUTES_WITH_ACTIONS'),
            value: 'minutesWithActions',
            icon: <MsWordIcon />
        },
        {
            label: i18n.t('EXPORT_OPTIONS_ACTIONS_ONLY'),
            value: 'actionsOnly',
            icon: <MsExcelIcon />
        }
    ]

    const handleSelect = (value: OptionType) => {
        setExportOption(value)
        if (value === 'actionsOnly') {
            setShouldShowTemplateList(false)
        } else {
            setShouldShowTemplateList(true)
        }
    }

    const handleKeyDown = (event: React.KeyboardEvent, value: OptionType) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleSelect(value)
        }
    }

    return (
        <div className={classes.container}>
            <Typography className={classes.templateTitle}>
                {i18n.t('EXPORT_OPTIONS_CONTENT')}
            </Typography>
            <div className={classes.contentContainer}>
                {options.map((option) => (
                    <div
                        key={option.value}
                        className={`${classes.card} ${
                            exportOption === option.value ? classes.selectedCard : ''
                        }`}
                        onClick={() => handleSelect(option.value)}
                        onKeyDown={(event) => handleKeyDown(event, option.value)}
                        tabIndex={0}
                        role="button"
                        aria-label={`Select ${option.label} export option`}
                        aria-pressed={exportOption === option.value}>
                        <span>{option.icon}</span>
                        <Typography className={classes.templateTitle}>{option.label}</Typography>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default ExportOptions
